import { createId } from './id';
import type { CaptionCue, CaptionSettings, ReelProject, WordTiming } from '@/types';

/**
 * Produces the CaptionCue list positioned in absolute timeline seconds
 * (i.e. shifted by the voiceover's startAt + silenceBefore), ready for the
 * renderer to look up "what caption is active at time t".
 */
export function getTimelineCaptionCues(project: ReelProject): CaptionCue[] {
  const { voiceover, captions } = project;
  if (!captions.enabled || !voiceover.wordTimings?.length) return [];

  const offset = voiceover.startAt + voiceover.silenceBefore - voiceover.trimStart;
  const shiftedWords: WordTiming[] = voiceover.wordTimings.map((w) => ({
    word: w.word,
    startSeconds: w.startSeconds + offset,
    endSeconds: w.endSeconds + offset,
  }));

  return buildCaptionCues(shiftedWords, captions);
}

/**
 * Estimates per-word timings when Google TTS does not return marks.
 *
 * Model: distribute `totalDurationSeconds` across words proportionally to each
 * word's "weight" (roughly its syllable count, approximated from character
 * count) and add extra pause weight after punctuation, so timing tracks
 * natural speech rhythm rather than pure word count.
 */
export function estimateWordTimings(script: string, totalDurationSeconds: number, speakingRate = 1): WordTiming[] {
  const tokens = tokenizeScript(script);
  if (tokens.length === 0 || totalDurationSeconds <= 0) return [];

  const weights = tokens.map((t) => wordWeight(t.word) + pauseWeight(t.trailingPunctuation));
  const totalWeight = weights.reduce((sum, w) => sum + w, 0) || tokens.length;

  const effectiveDuration = totalDurationSeconds; // speakingRate already reflected in audio duration
  let cursor = 0;
  const timings: WordTiming[] = tokens.map((t, i) => {
    const share = (weights[i] / totalWeight) * effectiveDuration;
    const start = cursor;
    const end = Math.min(effectiveDuration, cursor + share);
    cursor = end;
    return { word: t.word, startSeconds: start, endSeconds: end };
  });

  void speakingRate;
  return timings;
}

function tokenizeScript(script: string): Array<{ word: string; trailingPunctuation: boolean }> {
  const rawTokens = script.trim().split(/\s+/).filter(Boolean);
  return rawTokens.map((token) => ({
    word: token,
    trailingPunctuation: /[.,!?;:]$/.test(token),
  }));
}

function wordWeight(word: string): number {
  const letters = word.replace(/[^a-zA-Z]/g, '');
  // Approximate syllables via vowel-group count; minimum weight of 1.
  const syllables = letters.toLowerCase().match(/[aeiouy]+/g)?.length ?? 1;
  return Math.max(1, syllables);
}

function pauseWeight(hasTrailingPunctuation: boolean): number {
  return hasTrailingPunctuation ? 0.6 : 0;
}

/**
 * Groups word timings into on-screen caption cues, respecting maxCharsPerLine
 * and maxLines from CaptionSettings. Each cue spans the words it contains.
 */
export function buildCaptionCues(words: WordTiming[], settings: CaptionSettings): CaptionCue[] {
  if (words.length === 0) return [];

  const maxCharsPerCue = settings.maxCharsPerLine * settings.maxLines;
  const cues: CaptionCue[] = [];
  let current: WordTiming[] = [];
  let currentLength = 0;

  const flush = () => {
    if (current.length === 0) return;
    cues.push({
      id: createId('cue'),
      text: applyCasing(current.map((w) => w.word).join(' '), settings.casing),
      words: current,
      startSeconds: current[0].startSeconds,
      endSeconds: current[current.length - 1].endSeconds,
    });
    current = [];
    currentLength = 0;
  };

  for (const word of words) {
    const additionalLength = currentLength === 0 ? word.word.length : currentLength + 1 + word.word.length;
    const endsSentence = /[.!?]$/.test(word.word);

    if (additionalLength > maxCharsPerCue && current.length > 0) {
      flush();
      current.push(word);
      currentLength = word.word.length;
    } else {
      current.push(word);
      currentLength = additionalLength;
      if (endsSentence) flush();
    }
  }
  flush();

  return cues;
}

/**
 * Applies the mutually-exclusive casing transform. `original` performs no
 * transformation; `uppercase`/`lowercase` are mutually exclusive by type.
 */
export function applyCasing(text: string, casing: CaptionSettings['casing']): string {
  switch (casing) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'original':
    default:
      return text;
  }
}

/** Wraps a cue's text into display lines honoring maxCharsPerLine / maxLines. */
export function wrapCueLines(text: string, maxCharsPerLine: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxCharsPerLine && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) {
        // Last allowed line: append remaining words even if they overflow, then stop.
        const remainingIndex = words.indexOf(word);
        line = words.slice(remainingIndex).join(' ');
        lines.push(line);
        return lines.slice(0, maxLines);
      }
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}
